/**
 * The CarPlay control bridge.
 *
 * 🟥 WHY THIS EXISTS AT ALL. On CarPlay the app does NOT receive touch on the
 * map. Apple, verbatim: "Your app won't receive direct tap or drag events in
 * the base view." Gestures arrive only as CPMapTemplate callbacks, and plenty
 * of cars have no touchscreen in the first place — a Mercedes drives CarPlay
 * from a steering-wheel thumbpad and a console controller. Apple therefore
 * REQUIRES a button to enter panning mode: "since drag gestures are not
 * available in all vehicles".
 *
 * So on the car screen the buttons are not a fallback for pinch. They are the
 * only input that exists. Swift owns the buttons; this is the other end of the
 * wire, called through evaluateJavaScript.
 *
 * Kept deliberately tiny and string-keyed: it is called across a WebView
 * boundary from Swift, where a typo is a silent no-op rather than a compile
 * error, so the surface it can get wrong is worth minimising.
 */

export type CarPlayCommand =
  | 'hole:next'
  | 'hole:prev'
  | 'zoom:in'
  | 'zoom:out'
  | 'zoom:cycle'
  | 'pan:up'
  | 'pan:down'
  | 'pan:left'
  | 'pan:right'
  | 'pan:begin'
  | 'recentre'
  | 'mute:toggle';

export interface CarPlayHandlers {
  holeNext: () => void;
  holePrev: () => void;
  zoomIn: () => void;
  zoomOut: () => void;
  /** One button, three zoom levels: hole → closer → closest → back to hole. */
  zoomCycle?: () => void;
  /** Screen-fraction pan, matching the on-screen arrow buttons. */
  pan: (dx: number, dy: number) => void;
  /** Pixel pan from a finger drag on a touchscreen car (CarPlay pan gesture). */
  panPx?: (dx: number, dy: number) => void;
  /** Entering CarPlay panning mode: stop following or the map fights the thumb. */
  panBegin: () => void;
  recentre: () => void;
  /** The car's mute button: no tap reaches the page's own speaker icon. */
  muteToggle?: () => void;
}

declare global {
  interface Window {
    /** Called by CarPlaySceneDelegate. Returns true if the command was handled. */
    __gocaddyCommand?: (cmd: string) => boolean;
  }
}

/**
 * Register the handlers. Returns a cleanup that removes them.
 *
 * Swift checks the return value, so an unhandled command is visible in the
 * Xcode log rather than being swallowed — a dead CarPlay button that fails
 * silently is close to impossible to diagnose from inside a moving car.
 */
export function registerCarPlayCommands(h: CarPlayHandlers): () => void {
  const run = (cmd: string): boolean => {
    // The only console a car screen has: the page can show the last command.
    (window as unknown as { __gcLastCmd?: string }).__gcLastCmd = cmd;
    // A finger drag arrives as 'pan:px:<dx>,<dy>' in points, many times a
    // second — the one command that carries a payload.
    if (cmd.startsWith('pan:px:')) {
      const [dx, dy] = cmd.slice(7).split(',').map(Number);
      if (h.panPx && Number.isFinite(dx) && Number.isFinite(dy)) { h.panPx(dx, dy); return true; }
      return false;
    }
    switch (cmd as CarPlayCommand) {
      case 'hole:next': h.holeNext(); return true;
      case 'hole:prev': h.holePrev(); return true;
      case 'zoom:in': h.zoomIn(); return true;
      case 'zoom:out': h.zoomOut(); return true;
      case 'zoom:cycle': if (h.zoomCycle) { h.zoomCycle(); return true; } return false;
      // Direction is the way the MAP moves, matching the on-screen arrows: the
      // "up" button shows more of what is ahead, so the camera moves forward.
      case 'pan:up': h.pan(0, -1); return true;
      case 'pan:down': h.pan(0, 1); return true;
      case 'pan:left': h.pan(-1, 0); return true;
      case 'pan:right': h.pan(1, 0); return true;
      case 'pan:begin': h.panBegin(); return true;
      case 'recentre': h.recentre(); return true;
      case 'mute:toggle': if (h.muteToggle) { h.muteToggle(); return true; } return false;
      default: return false;
    }
  };

  window.__gocaddyCommand = run;
  return () => {
    if (window.__gocaddyCommand === run) delete window.__gocaddyCommand;
  };
}
